import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, BookOpen, Trash2, Clock } from 'lucide-react';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import { DataTable, useDataTableState } from '@/components/admin/DataTable';
import type { ColumnDef, BulkAction, PaginationData, DirtyCell } from '@/components/admin/DataTable';
import api from '@/lib/api';
import { formatCurrency, cn } from '@/lib/utils';
import { useUIStore } from '@/stores/ui-store';

interface Course {
  id: string;
  name: string;
  slug: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  price: number;
  compare_at_price: number | null;
  status: 'draft' | 'published' | 'archived';
  is_featured: boolean;
  enrollment_count: number;
  module_count: number;
  estimated_duration_hours: number | null;
  thumbnail_url: string | null;
  created_at: string;
  updated_at: string;
}

interface CoursesResponse {
  courses: Course[];
  pagination: PaginationData;
}

type StatusFilter = 'all' | 'published' | 'draft' | 'archived';

const getStatusBadge = (status: Course['status']) => {
  const variants = { published: 'green' as const, draft: 'yellow' as const, archived: 'gray' as const };
  return variants[status];
};

const getLevelBadge = (level: Course['level']) => {
  const variants = { beginner: 'green' as const, intermediate: 'blue' as const, advanced: 'red' as const };
  return variants[level];
};

const columns: ColumnDef<Course>[] = [
  {
    id: 'thumbnail',
    header: 'Image',
    hideable: false,
    width: 'w-16',
    cell: ({ row }) => (
      <Link to={`/admin/courses/${row.id}`} className="block">
        {row.thumbnail_url ? (
          <img src={row.thumbnail_url} alt="" className="w-12 h-12 object-cover rounded-lg" />
        ) : (
          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-gray-400" />
          </div>
        )}
      </Link>
    ),
  },
  {
    id: 'name',
    header: 'Course',
    accessorKey: 'name',
    sortable: true,
    editable: true,
    editType: 'text',
    cell: ({ row }) => (
      <Link to={`/admin/courses/${row.id}`} className="block group">
        <div className="font-medium text-gray-900 group-hover:text-blue-600">{row.name}</div>
        <div className="text-sm text-gray-500">{row.module_count} modules</div>
      </Link>
    ),
  },
  {
    id: 'level',
    header: 'Level',
    accessorKey: 'level',
    editable: true,
    editType: 'select',
    editOptions: [
      { label: 'Beginner', value: 'beginner' },
      { label: 'Intermediate', value: 'intermediate' },
      { label: 'Advanced', value: 'advanced' },
    ],
    cell: ({ row }) => (
      <Badge variant={getLevelBadge(row.level)}>
        {row.level.charAt(0).toUpperCase() + row.level.slice(1)}
      </Badge>
    ),
  },
  {
    id: 'price',
    header: 'Price',
    accessorKey: 'price',
    sortable: true,
    editable: true,
    editType: 'number',
    align: 'right',
    cell: ({ row }) => (
      <div className="text-right">
        <span className="font-medium text-gray-900">{formatCurrency(row.price)}</span>
        {row.compare_at_price != null && (
          <div className="text-xs text-gray-400 line-through">{formatCurrency(row.compare_at_price)}</div>
        )}
      </div>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    editable: true,
    editType: 'select',
    editOptions: [
      { label: 'Published', value: 'published' },
      { label: 'Draft', value: 'draft' },
      { label: 'Archived', value: 'archived' },
    ],
    cell: ({ row }) => (
      <Badge variant={getStatusBadge(row.status)}>
        {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
      </Badge>
    ),
  },
  {
    id: 'enrollment_count',
    header: 'Enrollments',
    accessorKey: 'enrollment_count',
    sortable: true,
    align: 'right',
    cell: ({ row }) => (
      <span className="font-medium text-gray-900">{row.enrollment_count}</span>
    ),
  },
  {
    id: 'duration',
    header: 'Duration',
    accessorKey: 'estimated_duration_hours',
    cell: ({ row }) => (
      row.estimated_duration_hours ? (
        <div className="flex items-center gap-1 text-sm text-gray-600">
          <Clock className="w-3.5 h-3.5" />
          {row.estimated_duration_hours}h
        </div>
      ) : (
        <span className="text-sm text-gray-400">--</span>
      )
    ),
  },
  {
    id: 'created_at',
    header: 'Created',
    accessorKey: 'created_at',
    sortable: true,
    hideable: true,
    defaultVisible: false,
    cell: ({ row }) => (
      <span className="text-sm text-gray-500">
        {new Date(row.created_at).toLocaleDateString()}
      </span>
    ),
  },
];

const Courses = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1, limit: 25, total: 0, total_pages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const addToast = useUIStore((s) => s.addToast);

  const { state, updateState, markDirty } = useDataTableState({
    storageKey: 'admin-courses',
    defaultSort: 'name',
    defaultLimit: 25,
    columns,
    filterKeys: ['status'],
  });

  const statusFilter = (state.filters.status || 'all') as StatusFilter;

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const params = new URLSearchParams();
        params.set('page', state.page.toString());
        params.set('limit', state.limit.toString());
        if (state.search) params.set('q', state.search);
        if (statusFilter !== 'all') params.set('status', statusFilter);
        if (state.sort) params.set('sort', state.sort);
        params.set('order', state.order);

        const response = await api.get<CoursesResponse>(`/admin/courses?${params.toString()}`);
        setCourses(response.courses);
        setPagination(response.pagination);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load courses');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourses();
  }, [state.page, state.limit, state.search, statusFilter, state.sort, state.order]);

  const handleStatusFilter = (status: StatusFilter) => {
    updateState({ filters: { ...state.filters, status: status === 'all' ? '' : status } });
  };

  const handleBatchSave = useCallback(async (changes: DirtyCell[]) => {
    const byRow = new Map<string, Record<string, unknown>>();
    for (const cell of changes) {
      if (!byRow.has(cell.rowId)) byRow.set(cell.rowId, {});
      const fields = byRow.get(cell.rowId)!;
      fields[cell.columnId] = cell.value;
    }

    const errors: string[] = [];
    for (const [rowId, fields] of byRow) {
      try {
        await api.put(`/admin/courses/${rowId}`, fields);
        setCourses((prev) =>
          prev.map((c) => (c.id === rowId ? { ...c, ...fields } : c))
        );
      } catch (err: any) {
        errors.push(err.message || `Failed to update course ${rowId}`);
      }
    }

    if (errors.length > 0) {
      addToast(`${errors.length} update(s) failed`, 'error');
      throw new Error('Some updates failed');
    }
    addToast(`${byRow.size} course(s) updated`, 'success');
  }, [addToast]);

  const handleDirtyChange = useCallback(
    (rowId: string, columnId: string, value: unknown, originalValue: unknown) => {
      markDirty(rowId, columnId, value, originalValue);
    },
    [markDirty]
  );

  const bulkActions: BulkAction<Course>[] = [
    {
      id: 'archive',
      label: 'Archive',
      icon: <Trash2 className="w-4 h-4 mr-1" />,
      variant: 'danger',
      confirmMessage: 'Are you sure you want to archive the selected courses?',
      onExecute: async (rows) => {
        const ids = rows.map((r) => r.id);
        await api.post('/admin/courses/bulk', { action: 'archive', ids });
        setCourses((prev) => prev.filter((c) => !ids.includes(c.id)));
        addToast(`${ids.length} course(s) archived`, 'success');
      },
    },
    {
      id: 'publish',
      label: 'Publish',
      variant: 'primary',
      onExecute: async (rows) => {
        const ids = rows.map((r) => r.id);
        await api.post('/admin/courses/bulk', { action: 'update_status', ids, data: { status: 'published' } });
        setCourses((prev) =>
          prev.map((c) => (ids.includes(c.id) ? { ...c, status: 'published' as const } : c))
        );
        addToast(`${ids.length} course(s) published`, 'success');
      },
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Courses</h1>
          <p className="text-gray-600 mt-1">Manage your course catalog</p>
        </div>
        <Link to="/admin/courses/new">
          <Button variant="primary" size="md">
            <Plus className="w-5 h-5 mr-2" />
            Add Course
          </Button>
        </Link>
      </div>

      <Card padding="md">
        <div className="flex gap-2 border-b border-gray-200">
          {(['all', 'published', 'draft', 'archived'] as StatusFilter[]).map((status) => (
            <button
              key={status}
              onClick={() => handleStatusFilter(status)}
              className={cn(
                'px-4 py-2 font-medium text-sm border-b-2 transition-colors',
                statusFilter === status
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              )}
              aria-current={statusFilter === status ? 'page' : undefined}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </Card>

      <DataTable
        columns={columns}
        data={courses}
        pagination={pagination}
        isLoading={isLoading}
        error={error}
        state={state}
        onStateChange={updateState}
        rowKey="id"
        onBatchSave={handleBatchSave}
        onDirtyChange={handleDirtyChange}
        bulkActions={bulkActions}
        searchPlaceholder="Search courses by name..."
        emptyState={
          <div className="p-12 text-center">
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {state.search || statusFilter !== 'all' ? 'No courses found' : 'No courses yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {state.search || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Get started by creating your first course'}
            </p>
            {!state.search && statusFilter === 'all' && (
              <Link to="/admin/courses/new">
                <Button variant="primary" size="md">
                  <Plus className="w-5 h-5 mr-2" />
                  Add Course
                </Button>
              </Link>
            )}
          </div>
        }
      />
    </div>
  );
};

export default Courses;
